import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Alert, 
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { parseString } from 'xml-js';

const MSProjectImport = ({ onImport }) => {
  const [uploadedData, setUploadedData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // Check file type
    const fileType = file.name.split('.').pop().toLowerCase();
    if (fileType !== 'xml' && fileType !== 'mpp') {
      setError('Please upload an MS Project XML file (.xml) or MS Project file (.mpp)');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const fileContent = e.target.result;
        
        // Parse XML
        parseString(fileContent, { explicitArray: false }, (err, result) => {
          if (err) {
            console.error('Error parsing XML:', err);
            setError('Failed to parse MS Project file. Please check the format and try again.');
            setLoading(false);
            return;
          }
          
          try {
            // Extract project data
            const project = result.Project;
            
            if (!project || !project.Tasks || !project.Tasks.Task) {
              setError('Invalid MS Project file format. Could not find tasks data.');
              setLoading(false);
              return;
            }
            
            // Extract tasks
            const tasks = Array.isArray(project.Tasks.Task) 
              ? project.Tasks.Task 
              : [project.Tasks.Task];
            
            // Extract calendar data for date calculations
            const startDate = project.StartDate 
              ? new Date(project.StartDate) 
              : new Date();
            
            // Process tasks
            const processedTasks = tasks
              .filter(task => task.Name && !task.Summary) // Filter out summary tasks
              .map(task => {
                // Calculate start and end dates
                const start = task.Start ? new Date(task.Start) : startDate;
                const duration = parseInt(task.Duration) || 0;
                const end = new Date(start);
                end.setDate(start.getDate() + duration);
                
                // Calculate progress
                const percentComplete = parseFloat(task.PercentComplete) || 0;
                
                return {
                  id: task.ID,
                  name: task.Name,
                  startDate: start,
                  endDate: end,
                  duration: duration,
                  progress: percentComplete,
                  weight: parseFloat(task.Weight) || 0
                };
              });
            
            // Generate weekly/monthly data points for S-Curve
            const datePoints = generateDatePoints(processedTasks, startDate);
            
            // Set uploaded data
            setUploadedData({
              tasks: processedTasks,
              datePoints: datePoints,
              chartData: {
                labels: datePoints.map(point => point.label),
                datasets: [
                  {
                    label: 'Planned Progress',
                    data: datePoints.map(point => point.planned),
                    borderColor: 'rgba(54, 162, 235, 1)',
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4
                  },
                  {
                    label: 'Actual Progress',
                    data: datePoints.map(point => point.actual),
                    borderColor: 'rgba(255, 159, 64, 1)',
                    backgroundColor: 'rgba(255, 159, 64, 0.2)',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4
                  }
                ]
              }
            });
          } catch (err) {
            console.error('Error processing MS Project data:', err);
            setError('Failed to process MS Project data. Please check the file format.');
          } finally {
            setLoading(false);
          }
        });
      } catch (err) {
        console.error('Error reading file:', err);
        setError('Failed to read file. Please try again.');
        setLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Failed to read file. Please try again.');
      setLoading(false);
    };
    
    reader.readAsText(file);
  };
  
  // Helper function to get the Tuesday of the week containing the given date
  const getTuesdayOfWeek = (date) => {
    const result = new Date(date);
    const dayOfWeek = result.getDay(); // 0 = Sunday, 1 = Monday, 2 = Tuesday, etc.
    
    if (dayOfWeek === 0) { // Sunday
      result.setDate(result.getDate() - 5); // Go back to Tuesday
    } else if (dayOfWeek === 1) { // Monday
      result.setDate(result.getDate() - 6); // Go back to previous Tuesday
    } else { // Tuesday (2) to Saturday (6)
      result.setDate(result.getDate() - (dayOfWeek - 2)); // Go back to Tuesday of current week
    }
    
    return result;
  };

  // Helper function to get the first Tuesday on or before the project start date
  const getFirstTuesday = (projectStartDate) => {
    return getTuesdayOfWeek(projectStartDate);
  };

  // Helper function to format week label with Tuesday-Monday range
  const formatWeekLabel = (weekStartDate, weekNumber) => {
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekStartDate.getDate() + 6); // Monday is 6 days after Tuesday
    
    const startDay = weekStartDate.getDate();
    const startMonth = weekStartDate.getMonth() + 1;
    const endDay = weekEndDate.getDate();
    const endMonth = weekEndDate.getMonth() + 1;
    
    return `Week ${weekNumber} (${startDay}/${startMonth} - ${endDay}/${endMonth})`;
  };

  // Generate date points for S-Curve
  const generateDatePoints = (tasks, startDate) => {
    if (!tasks || tasks.length === 0) return [];
    
    // Find project end date
    const endDate = new Date(Math.max(...tasks.map(task => task.endDate.getTime())));
    
    // Get the first Tuesday on or before project start date
    const firstTuesday = getFirstTuesday(startDate);
    
    // Calculate how many weeks we need from first Tuesday to project end
    const daysFromFirstTuesdayToEnd = Math.ceil((endDate - firstTuesday) / (1000 * 60 * 60 * 24));
    const weekCount = Math.ceil(daysFromFirstTuesdayToEnd / 7);
    
    // Calculate total project weight
    const totalWeight = tasks.reduce((sum, task) => sum + (task.weight || 0), 0);
    
    // If total weight is 0, distribute weight equally
    const normalizedTasks = tasks.map(task => {
      return {
        ...task,
        weight: totalWeight === 0 ? (100 / tasks.length) : (task.weight || 0)
      };
    });
    
    // Generate weekly data points (Tuesday to Monday format)
    const datePoints = [];
    
    for (let i = 0; i < weekCount; i++) {
      const weekStartDate = new Date(firstTuesday);
      weekStartDate.setDate(firstTuesday.getDate() + (i * 7));
      
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 6); // Monday is 6 days after Tuesday
      
      // Use the new format with date range
      const formattedDate = formatWeekLabel(weekStartDate, i + 1);
      
      // Calculate planned progress for this week
      let plannedProgress = 0;
      let actualProgress = 0;
      
      normalizedTasks.forEach(task => {
        // Check if task is active in this week
        if (task.startDate <= weekEndDate && task.endDate >= weekStartDate) {
          // Calculate task duration in days
          const taskDurationDays = Math.ceil((task.endDate - task.startDate) / (1000 * 60 * 60 * 24));
          
          // Calculate daily weight contribution
          const dailyWeight = task.weight / taskDurationDays;
          
          // Calculate days of task in this week
          const taskStartInWeek = task.startDate > weekStartDate ? task.startDate : weekStartDate;
          const taskEndInWeek = task.endDate < weekEndDate ? task.endDate : weekEndDate;
          const daysInWeek = Math.ceil((taskEndInWeek - taskStartInWeek) / (1000 * 60 * 60 * 24)) + 1;
          
          // Add planned progress for this week
          plannedProgress += dailyWeight * daysInWeek;
          
          // Add actual progress based on task completion
          const taskProgress = task.progress || 0;
          const taskContribution = task.weight * (taskProgress / 100);
          
          // Simplified actual progress calculation
          if (weekEndDate >= task.startDate) {
            const taskElapsedDays = Math.min(
              Math.ceil((weekEndDate - task.startDate) / (1000 * 60 * 60 * 24)),
              taskDurationDays
            );
            
            actualProgress += (taskElapsedDays / taskDurationDays) * taskContribution;
          }
        }
      });
      
      datePoints.push({
        label: `Week ${i + 1} (${formattedDate})`,
        planned: plannedProgress,
        actual: actualProgress
      });
    }
    
    // Convert to cumulative progress
    let plannedSum = 0;
    let actualSum = 0;
    
    return datePoints.map(point => {
      plannedSum += point.planned;
      actualSum += point.actual;
      
      return {
        label: point.label,
        planned: parseFloat(plannedSum.toFixed(2)),
        actual: parseFloat(actualSum.toFixed(2))
      };
    });
  };
  
  // Handle import
  const handleImport = () => {
    if (uploadedData) {
      onImport(uploadedData);
    }
  };
  
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#5D4037' }}>
        Import from MS Project
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Upload an MS Project XML file (.xml) or MS Project file (.mpp) to import your project schedule and generate an S-Curve.
        The system will extract tasks, durations, and progress information to create the S-Curve.
      </Alert>
      
      <Box sx={{ mb: 4 }}>
        <Button
          variant="contained"
          component="label"
          startIcon={<FileUploadIcon />}
          sx={{ 
            bgcolor: '#F0C14B', 
            '&:hover': { bgcolor: '#DDA726' },
            color: '#5D4037',
            fontWeight: 'bold'
          }}
        >
          Upload MS Project File
          <input
            type="file"
            accept=".xml,.mpp"
            hidden
            onChange={handleFileUpload}
          />
        </Button>
      </Box>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#F0C14B' }} />
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      
      {uploadedData && (
        <>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ mr: 1 }} />
              <Typography>
                Successfully parsed {uploadedData.tasks.length} tasks from the MS Project file. Review the data below and click "Import Data" to generate the S-Curve.
              </Typography>
            </Box>
          </Alert>
          
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Tasks Preview
          </Typography>
          
          <TableContainer component={Paper} elevation={0} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9F5EB' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Task Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>End Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Duration (days)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Progress (%)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Weight (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadedData.tasks.slice(0, 10).map((task, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{task.name}</TableCell>
                    <TableCell>{task.startDate.toLocaleDateString()}</TableCell>
                    <TableCell>{task.endDate.toLocaleDateString()}</TableCell>
                    <TableCell>{task.duration}</TableCell>
                    <TableCell>{task.progress}%</TableCell>
                    <TableCell>{task.weight.toFixed(2)}%</TableCell>
                  </TableRow>
                ))}
                {uploadedData.tasks.length > 10 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        Showing 10 of {uploadedData.tasks.length} tasks
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            S-Curve Data Preview
          </Typography>
          
          <TableContainer component={Paper} elevation={0} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9F5EB' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Period</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Planned Progress (%)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actual Progress (%)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Variance (%)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadedData.datePoints.map((point, index) => {
                  const variance = point.actual - point.planned;
                  
                  return (
                    <TableRow key={index} hover>
                      <TableCell>{point.label}</TableCell>
                      <TableCell>{point.planned}%</TableCell>
                      <TableCell>{point.actual}%</TableCell>
                      <TableCell sx={{ 
                        color: variance >= 0 ? '#4CAF50' : '#F44336',
                        fontWeight: 'bold'
                      }}>
                        {variance > 0 ? '+' : ''}{variance.toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => setUploadedData(null)}
              sx={{ 
                color: '#5D4037', 
                borderColor: '#5D4037',
                '&:hover': { 
                  backgroundColor: '#F9F5EB',
                  borderColor: '#8D6E63'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleImport}
              sx={{ 
                bgcolor: '#F0C14B', 
                '&:hover': { bgcolor: '#DDA726' },
                color: '#5D4037',
                fontWeight: 'bold'
              }}
            >
              Import Data
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default MSProjectImport;
